// Data.js

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useBlocker } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDashboard } from '../contexts/DashboardContext';
import { useAttributeCategories, useCategoryValues } from '../contexts/AttributesContext';
import {
  Paper,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  Tooltip,
  Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import SettingsIcon from '@mui/icons-material/Settings';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import HelpButton from '../components/common/HelpButton';
import LoadFailure from '../components/common/LoadFailure';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import { 
  DialogContentText, 
  DialogActions, 
  alpha
} from '@mui/material';
import DataTable from '../components/data/view/DataTable';
import DynamicFilters from '../components/data/view/DynamicFilters';
import ManageAttributes from '../components/data/manage/ManageAttributes';
import CreateTableForm from '../components/data/table_settings/CreateTableForm';
import DataCommandCenter from '../components/data/manage/DataCommandCenter';
import WorkspaceFilesHome from '../components/data/manage/WorkspaceFilesHome';
import TableSettings from '../components/data/table_settings/TableSettings';
import UnsavedSettingsDialog from '../components/data/table_settings/UnsavedSettingsDialog';
import canvasEditGuard from '../utils/canvasEditGuard';
import { isWizardDraftDirty } from '../utils/tableWizardDraft';
import { collection, doc, onSnapshot, getDoc, runTransaction } from 'firebase/firestore';
import { db } from '../services/firebase';
import SidebarFooter from '../components/common/SidebarFooter';
import LeftSidebar from '../components/common/LeftSidebar';
import EditIcon from '@mui/icons-material/Edit';
import { formatText } from '../components/common/TextFormatterProvider';
import { authenticatedFetch } from '../services/api_utils';
import { functionEndpoint } from '../services/endpoints';
import ConnectionsManager from '../components/data/connections/ConnectionsManager';
import TableNavList from '../components/data/TableNavList';
import { isExternalTable } from '../utils/tableTypes';
import {
  isTableBeingCreated, isTableBeingDeleted, isTableCreationInterrupted, isTableDeletionInterrupted,
  isTableUsable, msUntilATableStampExpires,
} from '../utils/tableLifecycle';
import { countNoun } from '../utils/pluralise';
import { diffValueEdits, applyValueEdits } from '../utils/mappingValueEdits';
import { validateIngestionRules } from '../utils/ingestionRuleValidation';
import { tableSettingsError } from '../utils/tableSettings';
import {
  tableOrderStorageKey,
  readStoredTableOrder,
  storeTableOrder,
  orderTables,
  reorderWithinSubset,
} from '../utils/tableOrder';

const HISTORY_DISCARD_CONFIRMATION =
  'Leaving this page will discard your unsaved table settings. Do you want to continue?';
const WIZARD_DISCARD_CONFIRMATION =
  'Leaving this page will discard the table you are creating. Do you want to continue?';

// The two refusals the settings save's transaction writes for the person. Any other
// failure (a security-rules refusal during a restore or a table deletion, a lost
// connection) is logged and shown as SETTINGS_NOT_SAVED_MESSAGE.
const TABLE_GONE_MESSAGE = 'This table no longer exists.';
const TABLE_REPLACED_MESSAGE = 'This table was deleted or replaced. Reopen its settings.';
const SETTINGS_NOT_SAVED_MESSAGE = 'Failed to save settings. Please try again.';

// The settings view edits copies of the saved table doc held in this page's state. Both the initial
// load and "discard changes" derive those copies here so the two can never disagree about what the
// saved value of a setting is.
const seedTableSettings = (table) => ({
  displaySettings: table.display_columns || {},
  possibleValues: table.possible_values || {},
  ingestionRules: table.ingestion_rules || [],
  displayOrder: table.display_order || null,
  showColumnTotals: (table.show_column_totals === undefined) ? true : !!table.show_column_totals,
  propagateMostRecent: !!table.propagate_most_recent,
  hidden: !!table.hidden,
  // The ingestion settings create_table writes (Spec 6 section 2.3): all four on a
  // time-series table, the date mode and the classification on a raw one, none on
  // an external one.
  columnPolicies: table.column_policies ?? null,
  reportingDateMode: table.reporting_date_mode ?? null,
  classification: table.classification ?? null,
  entityResolution: table.entityResolution ?? null,
  extractionPrompt: table.extraction_prompt || '',
  autoAllocateProfile: !!table.auto_allocate_profile,
});

// The slices above, by name. Dirtiness is tracked per slice (settingsDirty[tableId] is a map of
// these names) so the save writes only what this tab edited and the live table listener can
// keep refreshing the rest — a stale tab must never overwrite a setting another tab saved.
const ALL_SETTINGS_SLICES = Object.keys(seedTableSettings({}));

// The columns a table's display settings hide. `display_columns` maps a column (a metric
// inside a time-series period as `period.<name>`) to whether it is shown; a column with no
// entry is shown.
const hiddenColumnNames = (table) => (
  Object.entries(table?.display_columns || {})
    .filter(([, shown]) => shown === false)
    .map(([name]) => name)
    .sort()
);

// What the delete confirmation says depends on what is left of the table.
const deleteConfirmationText = (table) => {
  if (isTableCreationInterrupted(table)) {
    return 'Creating this table was interrupted before it was finished. Deleting it removes what was left behind and frees the name.';
  }
  if (isTableDeletionInterrupted(table)) {
    return 'An earlier deletion of this table was interrupted. Retrying finishes removing whatever is left of it. This cannot be undone.';
  }
  if (isExternalTable(table)) {
    return 'Removes this table from Decode. Your source data is not touched.';
  }
  return 'Are you sure you want to delete this table? This action cannot be undone. All data in the table will be permanently removed.';
};

// Overline-style header row shared by the sidebar's collapsible sections.
const sidebarGroupHeaderSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  pl: 1,
  pr: 0.5,
  py: 0.5,
  color: 'text.primary',
  '&:hover': {
    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
    borderRadius: 1
  }
};

const sidebarGroupTitleSx = {
  color: 'text.secondary',
  fontWeight: 600,
  letterSpacing: '0.08em',
  lineHeight: 2.2,
};

function Data() {
  const [error, setError] = useState('');
  // The tables listener failed: the sidebar says so instead of offering "Create your
  // first table", keeping the tables it already had. A failed listener has stopped,
  // so Retry resubscribes (tablesAttempt is in the effect's deps).
  const [tablesError, setTablesError] = useState(false);
  const [tablesAttempt, setTablesAttempt] = useState(0);
  const stampExpiryTimerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [tablesExpanded, setTablesExpanded] = useState(true);
  const [externalExpanded, setExternalExpanded] = useState(true);
  const [attributeExpanded, setAttributeExpanded] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [dialog, setDialog] = useState({ open: false, type: null });
  // The Create-table wizard's draft. `liveDraftRef` is what the mounted wizard
  // reports as it goes; `savedDraftRef` is the copy kept when the wizard closes
  // for Source Connections (its Manage button) and handed back when it
  // reopens, so the name, storage choice and step survive the detour. Any other
  // close discards it. `returnToCreateTable` shows the way back on the
  // connections view.
  const liveDraftRef = useRef(null);
  const savedDraftRef = useRef(null);
  const [returnToCreateTable, setReturnToCreateTable] = useState(false);
  // The wizard holds its draft only in memory, so an exit with anything
  // entered asks first (M211): a dismissal gets the Discard prompt below,
  // Back/Forward and unload the browser's confirm.
  const [wizardDirty, setWizardDirty] = useState(false);
  const [discardWizardOpen, setDiscardWizardOpen] = useState(false);
  const handleCreateTableDraftChange = useCallback((draft) => {
    liveDraftRef.current = draft;
    setWizardDirty(isWizardDraftDirty(draft));
  }, []);
  // True while the Create Table form's request is in flight: the dialog then refuses
  // Escape, the backdrop and its close controls, since closing would not cancel it.
  const [dialogBusy, setDialogBusy] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [expandedTableActions, setExpandedTableActions] = useState({});
  const [viewMode, setViewMode] = useState('data'); // 'data', 'files', 'settings'
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [filesNeedingAttention, setFilesNeedingAttention] = useState(0);
  const [blockedLandingBatches, setBlockedLandingBatches] = useState(new Map());
  const [expandControls, setExpandControls] = useState(null);

  const [displaySettings, setDisplaySettings] = useState({});
  const [possibleValues, setPossibleValues] = useState({});
  const possibleValuesBaselineRef = useRef({});
  const [ingestionRules, setIngestionRules] = useState({});
  const [newValueInputs, setNewValueInputs] = useState({});
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [success, setSuccess] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // Names the table a successful deletion removed. Kept apart from the settings
  // `success` flag: the selection moves to another table straight after a delete,
  // and that table's Settings page must not announce a save it never made (L75).
  const [deleteNotice, setDeleteNotice] = useState(null);
  const deleteNoticeTimerRef = useRef(null);
  const showDeleteNotice = (message) => {
    // A second deletion within the window replaces the notice and its timer,
    // so the first timer cannot dismiss the second notice early.
    clearTimeout(deleteNoticeTimerRef.current);
    setDeleteNotice(message);
    deleteNoticeTimerRef.current = setTimeout(() => setDeleteNotice(null), 6000);
  };
  useEffect(() => () => clearTimeout(deleteNoticeTimerRef.current), []);
  const [tableToDelete, setTableToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // What went wrong with a deletion, shown inside its confirmation dialog only and cleared
  // each time the dialog opens. It used to go into the settings `error`: a deletion moves the
  // selection to another table, so the message then sat on that table's Settings page, and
  // cancelling the dialog left it there (7.9).
  const [deleteError, setDeleteError] = useState(null);
  const [displayOrder, setDisplayOrder] = useState({});
  const [showColumnTotals, setShowColumnTotals] = useState({});
  const [propagateMostRecent, setPropagateMostRecent] = useState({});
  const [hiddenTables, setHiddenTables] = useState({});
  const [columnPolicies, setColumnPolicies] = useState({});
  const [reportingDateMode, setReportingDateMode] = useState({});
  const [classification, setClassification] = useState({});
  const [entityResolution, setEntityResolution] = useState({});
  const [extractionPrompts, setExtractionPrompts] = useState({});
  const [autoAllocateProfile, setAutoAllocateProfile] = useState({});
  const [settingsDirty, setSettingsDirty] = useState({});
  const [unsavedSettingsDialogOpen, setUnsavedSettingsDialogOpen] = useState(false);
  // Keyed by sidebar group ('decode' / 'external'), each group's hidden bucket expands independently.
  const [hiddenTablesExpanded, setHiddenTablesExpanded] = useState({});

  // Where the departure the user asked for waits while the unsaved-changes dialog is open.
  const pendingNavigationRef = useRef(null);
  // The Export tab's mapping draft: { dirty, validate, save, discard }, kept current by
  // ExportSettings. It is the 'exportMapping' slice of settingsDirty, saved after the table
  // document by saveSettings and closed by Discard.
  const exportDraftRef = useRef(null);

  const { companyName, currentUser } = useAuth();
  const {
    tables,
    setTables,
    currentTableId,
    setCurrentTableId,
    setTableData,
    tableData,
    fetchedByTable,
    setFetchedByTable,
    clearTableState
  } = useDashboard();

  // Get current table
  const currentTable = tables.find(table => table.id === currentTableId);
  const hasCurrentTable = Boolean(currentTable);
  // The live list, for handlers that only need a lookup and must not re-run on each snapshot.
  const tablesRef = useRef(tables);
  tablesRef.current = tables;
  const currentIsExternal = isExternalTable(currentTable);
  const effectiveViewMode = currentIsExternal && viewMode === 'files' ? 'data' : viewMode;
  // A table whose deletion was interrupted offers only a retry, but the
  // workspace and connections views are not about that table.
  const tableSpecificView = effectiveViewMode !== 'workspaceFiles' && effectiveViewMode !== 'connections';
  const showingInterruptedTable = tableSpecificView && isTableDeletionInterrupted(currentTable);
  // The selected name is only a creation's reservation: there is no table to show yet, or,
  // when the creation died, ever — that one can only be deleted.
  const showingUnfinishedCreation = tableSpecificView && currentTable?.status === 'Creating';
  const showingUnusableTable = showingInterruptedTable || showingUnfinishedCreation;
  // The successful query behind the rows on screen (see DashboardContext).
  const lastUsedFilters = currentTableId ? (fetchedByTable[currentTableId] || null) : null;
  
  // Get current table data
  const currentTableData = currentTableId ? (tableData[currentTableId] || []) : [];

  // Sidebar order: a per-user, per-browser preference (utils/tableOrder) that only
  // this sidebar reads — `tables` itself stays in fetched order for every other consumer.
  const tableOrderKey = tableOrderStorageKey(companyName, currentUser?.uid);
  const [tableOrder, setTableOrder] = useState([]);
  useEffect(() => {
    setTableOrder(readStoredTableOrder(tableOrderKey));
  }, [tableOrderKey]);

  // Sidebar groups: native Decode tables vs read-only external sources
  const orderedTables = orderTables(tables, tableOrder);
  const decodeTables = orderedTables.filter(table => !isExternalTable(table));
  const externalTables = orderedTables.filter(table => isExternalTable(table));
  // The sidebar keeps a table whose deletion was interrupted (for its retry);
  // the workspace must not route uploads to it. Memoised: the workspace keys
  // its listeners on this list.
  const usableTables = useMemo(() => tables.filter(isTableUsable), [tables]);

  // A drop within one group's visible rows: `groupTables` is that group's full ordered
  // list (visible + hidden), of which the visible rows are the reorderable subset.
  const handleReorderTables = (groupTables, dragIndex, dropIndex) => {
    const visibleIds = groupTables.filter(t => !hiddenTables[t.id]).map(t => t.id);
    const next = reorderWithinSubset(orderedTables.map(t => t.id), visibleIds, dragIndex, dropIndex);
    setTableOrder(next);
    storeTableOrder(tableOrderKey, next);
  };

  // The dirty map, readable from listeners without re-subscribing on every keystroke.
  const settingsDirtyRef = useRef(settingsDirty);
  settingsDirtyRef.current = settingsDirty;
  // Which company the tables listener last started for, so a Retry of that listener
  // (tablesAttempt) keeps the dirty map: dropping it would let the next snapshot hand an
  // edit in progress back to the server copy.
  const settingsCompanyRef = useRef(null);

  // Which incarnation of each table id the last snapshot carried (its createdAt). A table id
  // is its sanitised name, so "delete Premiums, create Premiums" reuses the id: the changed
  // createdAt is what tells the two apart. Compared as a primitive, since a Timestamp object
  // would differ by identity on every snapshot.
  const tableIncarnationsRef = useRef({});
  // Queries are numbered as they start: the number of the last one started, and of the last
  // one started for each table (see handleSubmit).
  const querySequenceRef = useRef(0);
  const latestQueryByTableRef = useRef({});
  const incarnationOf = (table) => {
    const created = table?.createdAt;
    if (created == null) return null;
    if (typeof created === 'string' || typeof created === 'number') return created;
    if (typeof created.toMillis === 'function') return created.toMillis();
    return JSON.stringify(created);
  };

  // Drop every piece of per-table state this page or the context keeps for one table id. Rows,
  // filters and dates live in the context; the rest is local. The settings slices are not
  // listed: the tables listener rebuilds those maps from the snapshot, so a gone table simply
  // stops appearing in them.
  const purgeTableState = (tableId) => {
    delete possibleValuesBaselineRef.current[tableId];
    clearTableState(tableId);
    const drop = (prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, tableId)) return prev;
      const next = { ...prev };
      delete next[tableId];
      return next;
    };
    setSettingsDirty(drop);
    setNewValueInputs(drop);
    setExpandedTableActions(drop);
  };

  // The tables list is live. One listener on the collection is the single source for the
  // list, the per-table settings maps, and the clean-up of a table that has gone:
  //   - `tables` always carries the current schema and settings of every table;
  //   - each settings slice map is rebuilt from the snapshot, keeping this tab's draft for any
  //     slice it is editing (the same rule as the Table Settings listener below);
  //   - a table that disappeared, or came back under the same name with a new createdAt, has
  //     all of its cached state purged, so a recreated table starts empty until its own query
  //     runs — from this tab or any other (H62);
  //   - a selection pointing at a gone table moves to the first visible table.
  // Nothing here fetches: consumers that fetch (data profiles, tag values) key on the table id
  // or on its column definitions, never on the table object, so a document write — the
  // backend learning a possible value on approval, say — only refreshes what it changed.
  useEffect(() => {
    if (!companyName) return undefined;
    if (settingsCompanyRef.current !== companyName) {
      settingsCompanyRef.current = companyName;
      possibleValuesBaselineRef.current = {};
      setSettingsDirty({});
    }
    // The baseline is the list the context still holds — it outlives this page, so a table
    // deleted (or recreated) while the user was elsewhere is caught by the first snapshot.
    const baseline = {};
    tablesRef.current.forEach(table => { baseline[table.id] = incarnationOf(table); });
    tableIncarnationsRef.current = baseline;
    const unsubscribe = onSnapshot(collection(db, `users/${companyName}/tables`), (snapshot) => {
      setTablesError(false);
      const allTables = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // A table being deleted right now is hidden; one whose deletion was
      // interrupted stays listed, offering only a retry of the deletion. A table
      // still being created is listed as a row that cannot be opened, so the name
      // is visibly taken; one whose creation was interrupted offers only its deletion.
      const tablesList = allTables.filter(table => !isTableBeingDeleted(table));
      // A table hidden behind a fresh Deleting stamp comes back when the stamp
      // goes stale, and a "Creating…" row becomes "Creation interrupted" when its
      // reservation does, even if no further snapshot arrives: re-subscribe then.
      clearTimeout(stampExpiryTimerRef.current);
      const msUntilExpiry = msUntilATableStampExpires(allTables);
      if (msUntilExpiry !== null) {
        stampExpiryTimerRef.current = setTimeout(() => setTablesAttempt(n => n + 1), msUntilExpiry);
      }

      const nextIncarnations = {};
      tablesList.forEach(table => { nextIncarnations[table.id] = incarnationOf(table); });
      const prevIncarnations = tableIncarnationsRef.current;
      const gone = Object.keys(prevIncarnations).filter(id => (
        !Object.prototype.hasOwnProperty.call(nextIncarnations, id)
        || nextIncarnations[id] !== prevIncarnations[id]
      ));
      tableIncarnationsRef.current = nextIncarnations;
      gone.forEach(purgeTableState);

      setTables(tablesList);

      // Each slice map is rebuilt from the snapshot, but a table whose saved value is
      // unchanged keeps its existing entry, and a map with nothing changed is returned as
      // is — so an unrelated document write re-renders nothing that reads these.
      const dirtyByTable = settingsDirtyRef.current;
      const seeds = {};
      tablesList.forEach(table => { seeds[table.id] = seedTableSettings(table); });
      ALL_SETTINGS_SLICES.forEach((slice) => {
        sliceSetters[slice](prev => {
          const next = {};
          let changed = Object.keys(prev).length !== tablesList.length;
          tablesList.forEach(table => {
            const had = Object.prototype.hasOwnProperty.call(prev, table.id);
            const keepDraft = had && !gone.includes(table.id) && dirtyByTable[table.id]?.[slice];
            const seed = seeds[table.id][slice];
            const unchanged = had && !gone.includes(table.id)
              && JSON.stringify(prev[table.id]) === JSON.stringify(seed);
            next[table.id] = (keepDraft || unchanged) ? prev[table.id] : seed;
            if (next[table.id] !== prev[table.id]) changed = true;
          });
          return changed ? next : prev;
        });
      });

      setCurrentTableId(prev => {
        if (prev && Object.prototype.hasOwnProperty.call(nextIncarnations, prev)) return prev;
        // A table still being created cannot be opened, so it is never picked here.
        const selectableTables = tablesList.filter(table => !isTableBeingCreated(table));
        if (selectableTables.length === 0) return null;
        // Prefer non-hidden tables for initial selection
        const visibleTable = selectableTables.find(t => !t.hidden);
        return visibleTable ? visibleTable.id : selectableTables[0].id;
      });
    }, (err) => {
      console.error('Error watching tables:', err);
      setTablesError(true);
    });
    return () => {
      unsubscribe();
      clearTimeout(stampExpiryTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName, setTables, setCurrentTableId, clearTableState, tablesAttempt]);

  // While Table Settings is open, follow the live table document: another tab, or the
  // backend's approval-time learning, may change it under this page. Each snapshot refreshes
  // the cached table and re-seeds every slice this tab has NOT edited; an edited slice keeps
  // its draft, and the save writes only those.
  useEffect(() => {
    if (effectiveViewMode !== 'settings' || !companyName || !currentTableId) return undefined;
    const tableRef = doc(db, `users/${companyName}/tables`, currentTableId);
    return onSnapshot(tableRef, (snap) => {
      if (!snap.exists()) return;
      const table = { id: snap.id, ...snap.data() };
      setTables(prev => prev.map(t => (t.id === table.id ? table : t)));
      const dirty = settingsDirtyRef.current[table.id] || {};
      seedSlices(table.id, table, ALL_SETTINGS_SLICES.filter(slice => !dirty[slice]));
    }, (err) => {
      console.error('Error watching table settings:', err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveViewMode, companyName, currentTableId, setTables]);

  // In files mode, the current table's files that need attention: its profiles'
  // file_stats.status_needs_attention counters summed (Spec 6 section 5), the
  // figure the files view's summary cards add up from the same profiles.
  useEffect(() => {
    setFilesNeedingAttention(0);

    if (viewMode !== 'files' || !currentTableId || !companyName || !hasCurrentTable || currentIsExternal) {
      return undefined;
    }

    const profilesRef = collection(db, `users/${companyName}/tables/${currentTableId}/data_profiles`);
    return onSnapshot(profilesRef, (snapshot) => {
      let total = 0;
      snapshot.docs.forEach((profileDoc) => {
        total += profileDoc.data().file_stats?.status_needs_attention || 0;
      });
      setFilesNeedingAttention(total);
    }, (err) => {
      console.error('Error watching the data profiles:', err);
    });
    // Keyed on whether the table exists, not the table object: the live tables list hands
    // out a new object on every document write, and this must not re-open the profile
    // listener each time.
  }, [viewMode, currentTableId, companyName, hasCurrentTable, currentIsExternal]);

  // One listener per Decode table on its mutation claim, claims/ingestion (Spec 3
  // section 1.1). A claim reads `blocked` when a landing committed its rows but
  // could not be finished, which nothing retries on its own (Spec 4 section 6):
  // while it does, the table's entry in the sidebar shows an alert, and the files
  // of the claim's batch say so in place of their status (FilesHome, ProfileDashboard).
  // Held as a Map from table id to the blocked claim's batch id, for blocked tables
  // only. Keyed on the tables' ids, so a write to a table document re-opens nothing.
  const decodeTableIdsKey = JSON.stringify(tables.filter(table => !isExternalTable(table)).map(table => table.id));
  useEffect(() => {
    setBlockedLandingBatches(new Map());
    if (!companyName) return undefined;

    const unsubscribers = JSON.parse(decodeTableIdsKey).map((tableId) => onSnapshot(
      doc(db, `users/${companyName}/tables/${tableId}/claims/ingestion`),
      (snap) => {
        const blockedBatchId = snap.exists() && snap.data().status === 'blocked' ? snap.data().batch_id : null;
        setBlockedLandingBatches((previous) => {
          if ((previous.get(tableId) ?? null) === blockedBatchId) return previous;
          const next = new Map(previous);
          if (blockedBatchId === null) {
            next.delete(tableId);
          } else {
            next.set(tableId, blockedBatchId);
          }
          return next;
        });
      },
      (err) => {
        console.error(`Error watching the ingestion claim of ${tableId}:`, err);
      },
    ));
    return () => unsubscribers.forEach(unsubscribe => unsubscribe());
  }, [companyName, decodeTableIdsKey]);

  useEffect(() => {
    if (viewMode === 'files' && currentIsExternal) {
      setViewMode('data');
    }
  }, [viewMode, currentIsExternal]);

  // The sidebar's attribute categories and values come from the live
  // AttributesProvider: a category or value created in the Attributes manager,
  // or by another user, appears without a reload. Only an EXPANDED category's
  // values are held open, and only while it is expanded.
  const { categories: attributeCategories } = useAttributeCategories();
  const expandedCategoryIds = useMemo(
    () => Object.keys(expandedCategories).filter(categoryId => expandedCategories[categoryId]),
    [expandedCategories],
  );
  const categoryValues = useCategoryValues(expandedCategoryIds);
  useEffect(() => {
    setExpandedCategories({});
  }, [companyName]);

  // One setter per settings slice, keyed by the seedTableSettings name.
  const sliceSetters = {
    displaySettings: setDisplaySettings,
    possibleValues: setPossibleValues,
    ingestionRules: setIngestionRules,
    displayOrder: setDisplayOrder,
    showColumnTotals: setShowColumnTotals,
    propagateMostRecent: setPropagateMostRecent,
    hidden: setHiddenTables,
    columnPolicies: setColumnPolicies,
    reportingDateMode: setReportingDateMode,
    classification: setClassification,
    entityResolution: setEntityResolution,
    extractionPrompt: setExtractionPrompts,
    autoAllocateProfile: setAutoAllocateProfile,
  };

  // Replace the named slices of one table's local settings with the saved values in `table`.
  // Used by discard (every slice) and the live listener (every slice not being edited).
  const seedSlices = (tableId, table, slices) => {
    const seed = seedTableSettings(table);
    slices.forEach((slice) => {
      sliceSetters[slice](prev => ({ ...prev, [tableId]: seed[slice] }));
    });
  };

  // Every setting reaches Firestore through the one batched save, so any edit to a settings slice
  // has to flag its table as dirty — for the navigation guard, and so the save knows which
  // slices to write. The map is never left empty: an entry is either absent/false or has keys.
  const markSettingsDirty = (tableId, slice) => {
    if (!tableId || !slice) return;
    settingsDirtyRef.current = {
      ...settingsDirtyRef.current,
      [tableId]: { ...settingsDirtyRef.current[tableId], [slice]: true },
    };
    setSettingsDirty(prev => ({
      ...prev,
      [tableId]: { ...(prev[tableId] || {}), [slice]: true }
    }));
  };

  const handleExportDraftDirtyChange = useCallback((dirty) => {
    if (!currentTableId) return;
    setSettingsDirty(prev => {
      const current = prev[currentTableId] || {};
      if (dirty) {
        if (current.exportMapping) return prev;
        return { ...prev, [currentTableId]: { ...current, exportMapping: true } };
      }
      if (!current.exportMapping) return prev;
      const { exportMapping, ...rest } = current;
      return { ...prev, [currentTableId]: Object.keys(rest).length > 0 ? rest : false };
    });
  }, [currentTableId]);

  const handleColumnToggle = (tableId, columnName) => {
    setDisplaySettings(prev => ({
      ...prev,
      [tableId]: {
        ...prev[tableId],
        [columnName]: !(prev[tableId]?.[columnName] ?? true)
      }
    }));
    markSettingsDirty(tableId, 'displaySettings');
  };

  const handleAddPossibleValue = (tableId, columnName) => {
    const inputKey = `${tableId}-${columnName}`;
    const newValue = newValueInputs[inputKey]?.trim();
    
    if (!newValue || savingRef.current) return;
    if (!Object.prototype.hasOwnProperty.call(possibleValuesBaselineRef.current, tableId)) {
      possibleValuesBaselineRef.current[tableId] = possibleValues[tableId] || {};
    }
    
    setPossibleValues(prev => ({
      ...prev,
      [tableId]: {
        ...prev[tableId],
        [columnName]: [...(prev[tableId]?.[columnName] || []), newValue]
      }
    }));
    
    // Clear the input
    setNewValueInputs(prev => ({
      ...prev,
      [inputKey]: ''
    }));

    markSettingsDirty(tableId, 'possibleValues');
  };

  const handleRemovePossibleValue = (tableId, columnName, valueIndex) => {
    if (savingRef.current) return;
    if (!Object.prototype.hasOwnProperty.call(possibleValuesBaselineRef.current, tableId)) {
      possibleValuesBaselineRef.current[tableId] = possibleValues[tableId] || {};
    }
    setPossibleValues(prev => ({
      ...prev,
      [tableId]: {
        ...prev[tableId],
        [columnName]: prev[tableId][columnName].filter((_, index) => index !== valueIndex)
      }
    }));
    markSettingsDirty(tableId, 'possibleValues');
  };

  const handleNewValueInputChange = (tableId, columnName, value) => {
    const inputKey = `${tableId}-${columnName}`;
    setNewValueInputs(prev => ({
      ...prev,
      [inputKey]: value
    }));
  };

  const buildCompleteDisplayColumns = (table, currentDisplay) => {
    if (!table) return currentDisplay || {};
    const existingDisplay = currentDisplay || {};
    const out = { ...existingDisplay };

    // Attribute columns
    (table.detailColumns || []).forEach(col => {
      const key = col?.name;
      if (!key) return;
      if (out[key] === undefined) out[key] = true;
    });

    // Metadata columns
    (table.attributeColumns || []).forEach(col => {
      const key = col?.name;
      if (!key) return;
      if (out[key] === undefined) out[key] = true;
    });

    // Table attribute columns (hidden by default)
    (table.tableAttributeColumns || []).forEach(col => {
      const key = col?.name;
      if (!key) return;
      if (out[key] === undefined) out[key] = false;
    });

    // Value leaves of a time-series table's period columns (a raw table has
    // none): handle one-level and two-level
    const schema = table.metricColumns?.schema || {};
    Object.entries(schema).forEach(([colName, colSchema]) => {
      if (!colName) return;
      const isLeaf = colSchema && typeof colSchema === 'object' && (
        Object.prototype.hasOwnProperty.call(colSchema, 'updateBehavior') ||
        Object.prototype.hasOwnProperty.call(colSchema, 'type')
      );
      if (isLeaf) {
        const key = `period.${colName}`;
        out[key] = existingDisplay[key] ?? true;
        return;
      }
      const nested = (colSchema && typeof colSchema === 'object' && colSchema.schema && typeof colSchema.schema === 'object')
        ? colSchema.schema
        : (colSchema || {});
      Object.keys(nested).forEach(subColName => {
        const key = `period.${colName}.${subColName}`;
        out[key] = existingDisplay[key] ?? true;
      });
    });

    return out;
  };

  // Returns whether the save landed; the unsaved-changes dialog only navigates away on a true.
  const saveSettings = async () => {
    if (!companyName || !currentTableId || savingRef.current) return false;
    savingRef.current = true;
    const exportDraft = exportDraftRef.current;

    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      const table = tables.find(t => t.id === currentTableId);
      const dirty = settingsDirty[currentTableId] || {};
      if (dirty.ingestionRules) {
        const ruleValidationError = validateIngestionRules(
          ingestionRules[currentTableId],
          table,
        );
        if (ruleValidationError) {
          setError(ruleValidationError);
          return false;
        }
      }
      // The ingestion settings are written whole and the landing pastes their values
      // into SQL, so the table document as it would be saved is checked as the
      // backend's validate_table_settings checks it (Spec 6 section 2.3).
      const settingsUpdates = {};
      if (dirty.columnPolicies) {
        settingsUpdates.column_policies = columnPolicies[currentTableId] ?? table.column_policies;
      }
      if (dirty.reportingDateMode) {
        settingsUpdates.reporting_date_mode = reportingDateMode[currentTableId] ?? table.reporting_date_mode;
      }
      if (dirty.classification) {
        settingsUpdates.classification = classification[currentTableId] ?? table.classification;
      }
      if (dirty.entityResolution) {
        settingsUpdates.entityResolution = entityResolution[currentTableId] ?? table.entityResolution;
      }
      if (Object.keys(settingsUpdates).length > 0) {
        const settingsError = tableSettingsError({ ...table, ...settingsUpdates });
        if (settingsError) {
          setError(settingsError);
          return false;
        }
      }
      // An open export-mapping draft is saved by this button too; it is refused up front, before
      // anything is written, for the same reasons the tab's own Save Mapping would refuse it.
      if (dirty.exportMapping) {
        const draftError = exportDraft?.validate?.();
        if (draftError) {
          setError(draftError);
          return false;
        }
      }

      // Only edited fields are written. Mapping values additionally merge the
      // person's additions and removals into the latest document in a transaction.
      const updates = { ...settingsUpdates };
      if (dirty.displaySettings) {
        updates.display_columns = buildCompleteDisplayColumns(table, displaySettings[currentTableId]);
      }
      const possibleValueEdits = dirty.possibleValues
        ? diffValueEdits(possibleValuesBaselineRef.current[currentTableId], possibleValues[currentTableId])
        : null;
      if (dirty.ingestionRules) updates.ingestion_rules = ingestionRules[currentTableId] || [];
      if (dirty.displayOrder) updates.display_order = displayOrder[currentTableId] || null;
      if (dirty.showColumnTotals) {
        updates.show_column_totals = (showColumnTotals[currentTableId] === undefined) ? true : !!showColumnTotals[currentTableId];
      }
      if (dirty.propagateMostRecent) updates.propagate_most_recent = !!(propagateMostRecent[currentTableId]);
      if (dirty.hidden) updates.hidden = !!(hiddenTables[currentTableId]);
      if (dirty.extractionPrompt) {
        updates.extraction_prompt = (extractionPrompts[currentTableId] ?? table?.extraction_prompt ?? '').trim();
      }
      if (dirty.autoAllocateProfile) {
        // live-doc fallback, not a bare false: a save landed before the async
        // seed must not overwrite a stored true with the map's default
        updates.auto_allocate_profile = !!(autoAllocateProfile[currentTableId] ?? table?.auto_allocate_profile);
      }

      if (Object.keys(updates).length > 0 || possibleValueEdits) {
        const tableRef = doc(db, `users/${companyName}/tables`, currentTableId);
        const savedUpdates = await runTransaction(db, async transaction => {
          const snapshot = await transaction.get(tableRef);
          if (!snapshot.exists()) throw new Error(TABLE_GONE_MESSAGE);
          const current = snapshot.data();
          if (!isTableUsable(current) || incarnationOf(current) !== incarnationOf(table)) {
            throw new Error(TABLE_REPLACED_MESSAGE);
          }
          const patch = { ...updates };
          if (possibleValueEdits) {
            patch.possible_values = applyValueEdits(current.possible_values || {}, possibleValueEdits);
          }
          transaction.update(tableRef, patch);
          return patch;
        });
        if (possibleValueEdits) {
          delete possibleValuesBaselineRef.current[currentTableId];
          setPossibleValues(previous => ({ ...previous, [currentTableId]: savedUpdates.possible_values }));
        }

        // Update local state to reflect the saved complete display map
        if (savedUpdates.display_columns) {
          setDisplaySettings(prev => ({
            ...prev,
            [currentTableId]: savedUpdates.display_columns
          }));
        }

        // Update local tables cache so DataTable gets the saved fields without refetch
        setTables(prev => prev.map(t => (
          t.id === currentTableId && incarnationOf(t) === incarnationOf(table) ? { ...t, ...savedUpdates } : t
        )));
      }

      // The mapping is its own document, written after the table document. If it fails, the
      // table settings that did save are no longer dirty and the draft alone stays flagged.
      if (dirty.exportMapping && exportDraft?.save) {
        try {
          await exportDraft.save();
        } catch (err) {
          console.error('Error saving export mapping:', err);
          setSettingsDirty(prev => ({ ...prev, [currentTableId]: { exportMapping: true } }));
          setError(`Settings saved, but the export mapping could not be saved: ${err.message}`);
          return false;
        }
      }

      setSettingsDirty(prev => ({
        ...prev,
        [currentTableId]: false
      }));

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      return true;
    } catch (err) {
      console.error('Error saving settings:', err);
      const isRefusal = err.message === TABLE_GONE_MESSAGE || err.message === TABLE_REPLACED_MESSAGE;
      setError(isRefusal ? `Failed to save settings: ${err.message}` : SETTINGS_NOT_SAVED_MESSAGE);
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // Settings edits only exist in this page's state until the batched save, so in-app departures from
  // the settings view go through here instead of calling setViewMode/setCurrentTableId directly.
  const requestSettingsNavigation = (navigate) => {
    if (effectiveViewMode !== 'settings' || !settingsDirty[currentTableId]) {
      navigate();
      return;
    }
    pendingNavigationRef.current = navigate;
    setUnsavedSettingsDialogOpen(true);
  };

  const runPendingNavigation = () => {
    const navigate = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    setUnsavedSettingsDialogOpen(false);
    navigate?.();
  };

  const handleSaveSettingsAndContinue = async () => {
    const saved = await saveSettings();
    // A rejected save leaves its error on the settings page, so drop the departure and let the user read it.
    if (!saved) {
      pendingNavigationRef.current = null;
      setUnsavedSettingsDialogOpen(false);
      return;
    }
    runPendingNavigation();
  };

  const handleDiscardSettings = () => {
    if (savingRef.current) return;
    delete possibleValuesBaselineRef.current[currentTableId];
    const table = tables.find(t => t.id === currentTableId);
    if (table) seedSlices(table.id, table, ALL_SETTINGS_SLICES);
    exportDraftRef.current?.discard?.();
    setSettingsDirty(prev => ({ ...prev, [currentTableId]: false }));
    runPendingNavigation();
  };

  const handleStayOnSettings = () => {
    pendingNavigationRef.current = null;
    setUnsavedSettingsDialogOpen(false);
  };

  // Departures that live outside this page — the header's page links, the sidebar's Settings gear,
  // Logout — consult the shared leave guard before proceeding. Registering here sends them through
  // the same Save / Discard / Stay dialog as this page's own table and view switches; "Save and
  // continue" and "Discard" then run the parked departure exactly as they run a table switch.
  const hasUnsavedSettings = effectiveViewMode === 'settings' && Boolean(settingsDirty[currentTableId]);
  useEffect(() => {
    canvasEditGuard.isDirty = () => hasUnsavedSettings;
    canvasEditGuard.request = (proceed) => {
      pendingNavigationRef.current = proceed;
      setUnsavedSettingsDialogOpen(true);
    };
    return () => {
      canvasEditGuard.isDirty = () => false;
      canvasEditGuard.request = null;
    };
  }, [hasUnsavedSettings]);

  // Browser Back/Forward is already in flight when we hear of it, so it cannot be parked behind the
  // dialog; it gets the browser's confirm instead, as the Agent page does for canvas edits.
  const historyBlocker = useBlocker(
    useCallback(
      ({ historyAction }) => historyAction === 'POP' && (hasUnsavedSettings || wizardDirty),
      [hasUnsavedSettings, wizardDirty]
    )
  );
  useEffect(() => {
    if (historyBlocker.state !== 'blocked') return;
    // The wizard is a modal in front of any settings, so its draft is the one
    // the person would lose first.
    if (window.confirm(wizardDirty ? WIZARD_DISCARD_CONFIRMATION : HISTORY_DISCARD_CONFIRMATION)) {
      setTimeout(historyBlocker.proceed, 0);
    } else {
      historyBlocker.reset();
    }
  }, [historyBlocker, wizardDirty]);

  // Closing the tab cannot be intercepted with our own dialog; browsers only offer their generic prompt.
  useEffect(() => {
    const hasUnsavedSettings = effectiveViewMode === 'settings' && Object.values(settingsDirty).some(Boolean);
    if (!hasUnsavedSettings && !wizardDirty) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [effectiveViewMode, settingsDirty, wizardDirty]);

  const handleDeleteClick = () => {
    setTableToDelete(currentTable);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTableToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!tableToDelete || !companyName) return;

    try {
      setDeleting(true);
      setDeleteError(null);

      const path = `users/${companyName}/tables/${tableToDelete.id}`;
      // A table's id is its name, so the request also says which table of that name it
      // means: the one created at this moment. The backend deletes nothing if that table is
      // already gone and another now carries the name. A reservation left by an interrupted
      // creation has no createdAt, which is sent as null.
      const response = await authenticatedFetch(functionEndpoint('delete_table'), {
        method: 'POST',
        body: JSON.stringify({ path, created_at: tableToDelete.createdAt ?? null })
      });

      if (!response.ok) {
        const errorText = await response.text();
        // The backend removes the table document as its very last step. While the document
        // exists the deletion did not finish, and the dialog stays open with the reason so
        // the user can retry. If it is gone the table is deleted, whatever this response
        // says (another attempt may have finished it first), and is reported as deleted.
        let stillExists;
        try {
          stillExists = (await getDoc(doc(db, path))).exists();
        } catch (existenceCheckError) {
          // Whether the document is gone could not be read, so the backend's own reason is
          // what the user is shown, not the failure of this follow-up read.
          console.error('Could not check whether the table still exists:', existenceCheckError);
          stillExists = true;
        }
        if (stillExists) throw new Error(errorText || 'Failed to delete table');
      }

      // The tables listener sees the document go: it drops the table from the list and
      // every settings map, purges its cached rows, filters and dates, and moves the
      // selection — the same path a deletion made in another tab takes.
      showDeleteNotice(`Table "${tableToDelete.name || tableToDelete.id}" was deleted.`);
      setDeleteDialogOpen(false);
      setTableToDelete(null);
    } catch (err) {
      console.error('Error deleting table:', err);
      setDeleteError(`Failed to delete table: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // Toggle category expansion
  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const filterDataByDisplayColumns = (data, displayColumns) => {
    // TODO: Implement this
    return data;
  };

  const handleSubmit = async (filters, rangeFilters = {}) => {
    if (!currentTableId || !companyName) return;

    // The table this query is for can be deleted, or deleted and created again under the same
    // name, before the answer arrives. The tables listener has by then cleared what was cached
    // for it, and the late answer must not put the old table's rows back under the name (7.16).
    const tableId = currentTableId;
    const incarnationAtStart = tableIncarnationsRef.current[tableId];
    const tableIsStillTheOneQueried = () => (
      Object.prototype.hasOwnProperty.call(tableIncarnationsRef.current, tableId)
      && tableIncarnationsRef.current[tableId] === incarnationAtStart
    );
    // The backend leaves a hidden column out of what it reads, so the rows that come back
    // cannot show it. Remembered with the rows, so revealing one of these later is noticed.
    const tableAtStart = tablesRef.current.find(table => table.id === tableId);
    const hiddenColumnsAtFetch = hiddenColumnNames(tableAtStart);
    // Two queries for one table can be in flight at once (the user applies new filters while
    // the re-run below is still reading). Answers can arrive in either order, so only the
    // query started last for a table may fill it, and only the query started last of all
    // may say the page has stopped loading.
    querySequenceRef.current += 1;
    const querySequence = querySequenceRef.current;
    latestQueryByTableRef.current[tableId] = querySequence;
    const isLatestQueryForItsTable = () => latestQueryByTableRef.current[tableId] === querySequence;

    setLoading(true);
    setError('');

    try {
      const response = await authenticatedFetch(functionEndpoint('get_table_data'), {
        method: 'POST',
        body: JSON.stringify({
          company_id: companyName,
          table_id: tableId,
          filters,
          range_filters: rangeFilters
        })
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        let message = errorText || 'Failed to fetch data';
        try {
          const parsed = JSON.parse(errorText);
          message = parsed?.error?.message || parsed?.message || message;
        } catch (_) {
          // Keep the plain-text backend message.
        }
        throw new Error(message);
      }

      const data = await response.json();
      if (!tableIsStillTheOneQueried() || !isLatestQueryForItsTable()) return;

      const rawRows = data.rows || [];
      const truncated = !!data.truncated;

      // Rows and the query that produced them land together. A request that
      // fails above leaves both as they were, so the previous rows keep their
      // own filters for the truncation notice and the exports.
      setTableData(prev => ({
        ...prev,
        [tableId]: rawRows
      }));
      setFetchedByTable(prev => ({
        ...prev,
        [tableId]: {
          filters,
          rangeFilters,
          hiddenColumnsAtFetch,
          truncated,
          rowCount: data.row_count ?? rawRows.length,
          shownRows: rawRows.length,
          forwardFilledFrom: data.forward_filled_from,
        }
      }));
    } catch (err) {
      console.error('Error fetching data:', err);
      // A failure about a table that is gone is not shown over whichever table is open now,
      // and neither is the failure of a query that a later one has replaced.
      if (!tableIsStillTheOneQueried() || !isLatestQueryForItsTable()) return;
      setError(err.message || 'Failed to fetch data');
    } finally {
      if (querySequenceRef.current === querySequence) setLoading(false);
    }
  };

  // Rows fetched while a column was hidden do not hold that column. When the column is shown
  // again (saved in Table Settings, by this user or another) the grid would draw it blank, so
  // the last applied query is run again as soon as View Data is on screen (7.13). Hiding a
  // column needs no new read. A re-run that fails leaves `lastUsedFilters` as it was, so this
  // does not fire again until something it depends on changes.
  const hiddenColumnsNow = hiddenColumnNames(currentTable);
  // A primitive, so the effect re-runs when the set of hidden columns changes and not on
  // every snapshot that replaces the table object.
  const hiddenColumnsNowKey = hiddenColumnsNow.join('|');
  useEffect(() => {
    if (effectiveViewMode !== 'data' || showingUnusableTable || !lastUsedFilters) return;
    const aColumnWasRevealed = lastUsedFilters.hiddenColumnsAtFetch.some(
      name => !hiddenColumnsNow.includes(name)
    );
    if (!aColumnWasRevealed) return;
    handleSubmit(lastUsedFilters.filters, lastUsedFilters.rangeFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveViewMode, showingUnusableTable, currentTableId, hiddenColumnsNowKey, lastUsedFilters]);

  // Handle dialog open/close
  const handleOpenDialog = (type) => {
    setDialog({ open: true, type });
  };

  const handleCloseDialog = () => {
    setDialog({ open: false, type: null });
    setDialogBusy(false);
    savedDraftRef.current = null;
    setReturnToCreateTable(false);
    setWizardDirty(false);
    setDiscardWizardOpen(false);
  };

  // Manage, from the wizard's Source step: the wizard closes for Source
  // Connections but its draft is kept, and the connections view offers the way
  // back.
  const handleManageConnectionsFromWizard = () => {
    const draft = liveDraftRef.current;
    handleCloseDialog();
    savedDraftRef.current = draft;
    setReturnToCreateTable(true);
    requestSettingsNavigation(() => setViewMode('connections'));
  };

  // The user's dismissal (Escape, backdrop, close icon, Cancel) — ignored while
  // busy, and asked about when the wizard holds a draft (M211).
  const handleDismissDialog = () => {
    if (dialogBusy) return;
    if (dialog.type === 'createTable' && wizardDirty) {
      setDiscardWizardOpen(true);
      return;
    }
    handleCloseDialog();
  };

  // Handle table selection with auto-expansion of actions
  const handleTableSelect = (tableId) => {
    const targetTable = tables.find(table => table.id === tableId);
    const applySelection = () => {
      setCurrentTableId(tableId);
      if (isExternalTable(targetTable)) {
        setViewMode('data');
      }
      // Auto-expand actions for the selected table
      setExpandedTableActions(prev => ({
        ...prev,
        [tableId]: true
      }));
    };

    // Re-clicking the open table only expands its action list, which leaves the settings view in
    // place - unless the table is external, where selection also forces the data view.
    if (tableId === currentTableId && !isExternalTable(targetTable)) {
      applySelection();
      return;
    }
    requestSettingsNavigation(applySelection);
  };

  const toggleTableActions = (tableId) => {
    setExpandedTableActions(prev => ({
      ...prev,
      [tableId]: !prev[tableId]
    }));
  };

  // Submenu navigation for the sidebar table items; entering settings needs no
  // guard because the guard protects leaving it with unsaved edits.
  const navigateToViewMode = (mode) => {
    if (mode === 'settings') {
      setViewMode('settings');
      return;
    }
    requestSettingsNavigation(() => setViewMode(mode));
  };

  // Right rail width; collapses to a slim 56px strip. (The left rail is the
  // shared LeftSidebar, which sizes itself.)
  const filtersWidth = { md: showFilters ? '25%' : '56px', lg: showFilters ? '280px' : '56px' };

  return (
    <Box 
      sx={{ 
        height: '100%', 
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Flush left sidebar */}
      <Box
        sx={{
          height: '100%',
          flexGrow: 1,
          m: 0,
          width: '100%',
          display: 'flex',
        }}
      >
        {/* Left Sidebar - Completely flush with left edge */}
        <LeftSidebar>
          <Box
            sx={{
              p: 2,
              pt: 1.5,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Everything above the footer scrolls as one column when the table lists or the
                attributes categories outgrow the rail; the footer stays pinned at the bottom. */}
            <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<CloudUploadIcon fontSize="small" />}
                onClick={() => requestSettingsNavigation(() => setViewMode('workspaceFiles'))}
                sx={{
                  mb: 2.5,
                  borderRadius: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  backgroundColor: (theme) => viewMode === 'workspaceFiles' ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                }}
              >
                Upload Files
              </Button>

              {/* Decode Tables group */}
              <Box sx={{ mb: 2 }}>
                <Box sx={sidebarGroupHeaderSx} onClick={() => setTablesExpanded(!tablesExpanded)}>
                  <Typography variant="overline" sx={sidebarGroupTitleSx}>Decode Tables</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title="Create table">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDialog('createTable');
                        }}
                        sx={{
                          p: 0.5,
                          mr: 0.5,
                          color: 'text.secondary',
                          '&:hover': { color: 'primary.main' }
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" sx={{ p: 0.5, color: 'text.secondary' }}>
                      {tablesExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                </Box>

                <Collapse in={tablesExpanded}>
                  <TableNavList
                    tables={decodeTables}
                    hiddenMap={hiddenTables}
                    hiddenExpanded={!!hiddenTablesExpanded.decode}
                    onToggleHiddenExpanded={() => setHiddenTablesExpanded(prev => ({ ...prev, decode: !prev.decode }))}
                    currentTableId={currentTableId}
                    viewMode={viewMode}
                    expandedTableActions={expandedTableActions}
                    filesNeedingAttention={filesNeedingAttention}
                    blockedLandingBatches={blockedLandingBatches}
                    onSelectTable={handleTableSelect}
                    onToggleActions={toggleTableActions}
                    onNavigate={navigateToViewMode}
                    onReorder={(from, to) => handleReorderTables(decodeTables, from, to)}
                  />
                  {tablesError && (
                    <LoadFailure thing="tables" onRetry={() => setTablesAttempt((n) => n + 1)} sx={{ p: 1 }} />
                  )}
                  {decodeTables.length === 0 && !tablesError && (
                    <ListItemButton sx={{ pl: 2, borderRadius: 1 }} onClick={() => handleOpenDialog('createTable')}>
                      <ListItemText
                        primary="Create your first table"
                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                      />
                    </ListItemButton>
                  )}
                </Collapse>
              </Box>

              {/* External Sources group */}
              <Box sx={{ mb: 2 }}>
                <Box sx={sidebarGroupHeaderSx} onClick={() => setExternalExpanded(!externalExpanded)}>
                  <Typography variant="overline" sx={sidebarGroupTitleSx}>External Sources</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title="Manage connections">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          requestSettingsNavigation(() => setViewMode('connections'));
                        }}
                        sx={{
                          p: 0.5,
                          mr: 0.5,
                          color: viewMode === 'connections' ? 'primary.main' : 'text.secondary',
                          '&:hover': { color: 'primary.main' }
                        }}
                      >
                        <SettingsIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" sx={{ p: 0.5, color: 'text.secondary' }}>
                      {externalExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                </Box>

                <Collapse in={externalExpanded}>
                  <TableNavList
                    tables={externalTables}
                    hiddenMap={hiddenTables}
                    hiddenExpanded={!!hiddenTablesExpanded.external}
                    onToggleHiddenExpanded={() => setHiddenTablesExpanded(prev => ({ ...prev, external: !prev.external }))}
                    currentTableId={currentTableId}
                    viewMode={viewMode}
                    expandedTableActions={expandedTableActions}
                    filesNeedingAttention={filesNeedingAttention}
                    blockedLandingBatches={blockedLandingBatches}
                    onSelectTable={handleTableSelect}
                    onToggleActions={toggleTableActions}
                    onNavigate={navigateToViewMode}
                    onReorder={(from, to) => handleReorderTables(externalTables, from, to)}
                  />
                  {externalTables.length === 0 && (
                    <ListItemButton
                      sx={{ pl: 2, borderRadius: 1 }}
                      onClick={() => requestSettingsNavigation(() => setViewMode('connections'))}
                    >
                      <ListItemText
                        primary="No live sources connected"
                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                      />
                    </ListItemButton>
                  )}
                </Collapse>
              </Box>
            
              {/* Spacer to push the footer to the bottom */}
              <Box sx={{ flexGrow: 1 }} />
            
              {/* Metadata Section - Moved to bottom */}
              <Box sx={{ mb: 2 }}>
                <Box sx={sidebarGroupHeaderSx} onClick={() => setAttributeExpanded(!attributeExpanded)}>
                  <Typography variant="overline" sx={sidebarGroupTitleSx}>Attributes</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDialog('attribute');
                      }}
                      sx={{ 
                        p: 0.5,
                        mr: 0.5,
                        color: 'text.secondary',
                        '&:hover': { color: 'primary.main' }
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAttributeExpanded(!attributeExpanded);
                      }}
                      sx={{ 
                        p: 0.5,
                        color: 'text.secondary'
                      }}
                    >
                      {attributeExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                </Box>
              
                <Collapse in={attributeExpanded}>
                  <List component="div" disablePadding>
                    {attributeCategories.map((category) => (
                      <React.Fragment key={category.id}>
                        <ListItemButton 
                          sx={{ 
                            pl: 2,
                            borderRadius: 1,
                            mb: 0.5,
                            '&:hover': {
                              backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'
                            }
                          }}
                          onClick={() => toggleCategoryExpansion(category.id)}
                        >
                          <ListItemText 
                            primary={formatText(category.name)} 
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleCategoryExpansion(category.id);
                            }}
                            sx={{ p: 0.5 }}
                          >
                            {expandedCategories[category.id] ? <ExpandLessIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                          </IconButton>
                        </ListItemButton>
                      
                        {/* Category Values */}
                        <Collapse in={expandedCategories[category.id]} timeout="auto" unmountOnExit>
                          <List 
                            component="div" 
                            disablePadding
                            sx={{ 
                              maxHeight: 240, 
                              overflowY: 'auto' 
                            }}
                          >
                            {categoryValues[category.id]?.map((value) => (
                              <ListItemButton 
                                key={value.id}
                                sx={{ pl: 4, py: 0.5 }}
                              >
                                <ListItemText 
                                  primary={formatText(value.name || value.value || value.id)} 
                                  primaryTypographyProps={{ variant: 'body2' }}
                                />
                              </ListItemButton>
                            )) || (
                              <ListItemButton sx={{ pl: 4, py: 0.5 }}>
                                <ListItemText 
                                  primary="Loading values..." 
                                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                                />
                              </ListItemButton>
                            )}
                            {categoryValues[category.id]?.length === 0 && (
                              <ListItemButton sx={{ pl: 4, py: 0.5 }}>
                                <ListItemText 
                                  primary="No values defined" 
                                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                                />
                              </ListItemButton>
                            )}
                          </List>
                        </Collapse>
                      </React.Fragment>
                    ))}
                  
                    {attributeCategories.length === 0 && (
                      <ListItemButton 
                        sx={{ 
                          pl: 2,
                          borderRadius: 1,
                          '&:hover': {
                            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'
                          }
                        }} 
                        onClick={() => handleOpenDialog('attribute')}
                      >
                        <ListItemText 
                          primary="No categories defined" 
                          primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                        />
                      </ListItemButton>
                    )}
                  </List>
                </Collapse>
              </Box>
            </Box>
            
            {/* Sidebar Footer */}
            <SidebarFooter page="data" />
          </Box>
        </LeftSidebar>

        {/* Main Content Area - Takes up whatever the rails leave over */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          flexGrow: 1,
          flexBasis: 0,
          minWidth: 0,
        }}>
          {deleteNotice && (
            <Alert severity="success" onClose={() => setDeleteNotice(null)} sx={{ m: 2, mb: 0, flexShrink: 0 }}>
              {deleteNotice}
            </Alert>
          )}
          {showingUnfinishedCreation ? (
            <Box sx={{ p: 3 }}>
              {isTableCreationInterrupted(currentTable) ? (
                // create_table died before its last step. Only the name reservation (and
                // perhaps an empty BigQuery table) exists; deleting it removes both.
                <Alert
                  severity="warning"
                  action={(
                    <Button color="inherit" size="small" onClick={handleDeleteClick}>
                      Delete
                    </Button>
                  )}
                >
                  <Typography variant="subtitle2">Creating "{currentTable.name}" was interrupted.</Typography>
                  <Typography variant="body2">
                    The table was never finished, so there is nothing to open. Delete it to free
                    the name, or create a table with the same name again to finish it.
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="info">"{currentTable.name}" is still being created.</Alert>
              )}
            </Box>
          ) : showingInterruptedTable ? (
            // A deletion that began and did not finish. Parts of the table may already be
            // gone, so nothing else is offered: the retry resumes where the attempt stopped.
            <Box sx={{ p: 3 }}>
              <Alert
                severity="warning"
                action={(
                  <Button color="inherit" size="small" onClick={handleDeleteClick}>
                    Retry deletion
                  </Button>
                )}
              >
                <Typography variant="subtitle2">Deleting "{currentTable.name}" was interrupted.</Typography>
                <Typography variant="body2">
                  Some of its data may already be gone, so this table can only be deleted. Retry the
                  deletion to finish removing it.
                </Typography>
                {currentTable.deletion_error && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Last attempt: {currentTable.deletion_error}
                  </Typography>
                )}
              </Alert>
            </Box>
          ) : effectiveViewMode === 'data' ? (
            <Box sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              p: 2,
            }}>
              {/* Main Data Table */}
              <Paper 
                sx={{ 
                  p: 2, 
                  flex: 1,
                  display: 'flex', 
                  flexDirection: 'column',
                  minHeight: '88vh',
                  overflow: 'hidden',
                  mb: 2,
                  borderRadius: 1.5,
                  border: (theme) => `1px solid ${theme.palette.divider}`
                }}
              >
                {currentTable ? (
                  <>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      mb: 1 
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {currentTable.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HelpButton 
                          docPath="category/6-viewing--filtering-table-data" 
                          tooltip="View Data Documentation"
                        />
                        <Tooltip title="Expand">
                          <IconButton size="small" onClick={() => expandControls?.open?.()} sx={{ mr: 0.5 }}>
                            <OpenInFullIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {/* Template export moved to DataTable footer menu */}
                      </Box>
                    </Box>
                    {lastUsedFilters?.truncated && (
                      <Alert severity="info" sx={{ mb: 1 }}>
                        Showing the first {countNoun(lastUsedFilters?.shownRows ?? currentTableData.length, 'row')} from the source - narrow your filters or set a date range to see specific data.
                      </Alert>
                    )}
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                      <DataTable
                        tableSchema={currentTable}
                        data={currentTableData}
                        companyId={companyName}
                        tableId={currentTableId}
                        loading={loading}
                        fetched={Boolean(lastUsedFilters)}
                        forwardFilledFrom={lastUsedFilters?.forwardFilledFrom}
                        bindExpandControls={setExpandControls}
                        exportHeaders={(() => {
                          const banned = new Set(['startDate','endDate','start_date','end_date']);
                          const src = lastUsedFilters?.filters || {};
                          const out = {};
                          Object.entries(src).forEach(([k,v]) => {
                            if (banned.has(k)) return;
                            // An Empty selection travels as a null (M203); the export summary names it.
                            out[k] = Array.isArray(v) ? v.map((item) => (item === null ? 'Empty' : item)) : v;
                          });
                          Object.entries(lastUsedFilters?.rangeFilters || {}).forEach(([col, range]) => {
                            out[col] = `${range.gte || '…'} → ${range.lte || '…'}`;
                          });
                          return out;
                        })()}
                        exportContext={{
                          companyId: companyName,
                          tableId: currentTableId,
                          tableName: currentTable?.name,
                          filters: { ...(lastUsedFilters?.filters || {}) },
                          rangeFilters: lastUsedFilters?.rangeFilters || {}
                        }}
                      />
                    </Box>
                  </>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography variant="body1" color="text.secondary">
                      Select a table from the sidebar to view data
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          ) : effectiveViewMode === 'workspaceFiles' ? (
            <WorkspaceFilesHome companyId={companyName} tables={usableTables} hiddenTables={hiddenTables} />
          ) : effectiveViewMode === 'connections' ? (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {returnToCreateTable && (
                <Alert
                  severity="info"
                  sx={{ mb: 1, flexShrink: 0 }}
                  action={(
                    <Button color="inherit" size="small" onClick={() => handleOpenDialog('createTable')}>
                      Return to table creation
                    </Button>
                  )}
                >
                  Your table draft is waiting. Create or correct the connection here, then return to finish the table.
                </Alert>
              )}
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <ConnectionsManager companyId={companyName} />
              </Box>
            </Box>
          ) : effectiveViewMode === 'files' ? (
            <DataCommandCenter 
              companyId={companyName}
              tableId={currentTableId}
              table={currentTable}
              blockedLandingBatchId={blockedLandingBatches.get(currentTableId) ?? null}
              onBack={() => setViewMode('data')}
            />
          ) : effectiveViewMode === 'settings' ? (
            <TableSettings
              key={`${companyName}/${currentTableId}/${incarnationOf(currentTable)}`}
              currentTable={currentTable}
              companyName={companyName}
              error={error}
              setError={setError}
              success={success}
              setSuccess={setSuccess}
              possibleValues={possibleValues}
              newValueInputs={newValueInputs}
              handleNewValueInputChange={handleNewValueInputChange}
              handleAddPossibleValue={handleAddPossibleValue}
              handleRemovePossibleValue={handleRemovePossibleValue}
              displaySettings={displaySettings}
              handleColumnToggle={handleColumnToggle}
              ingestionRules={ingestionRules}
              setIngestionRules={(updater) => {
                setIngestionRules(updater);
                markSettingsDirty(currentTableId, 'ingestionRules');
              }}
              saveSettings={saveSettings}
              saving={saving}
              handleDeleteClick={handleDeleteClick}
              displayOrder={displayOrder[currentTableId] || null}
              onDisplayOrderChange={(newOrder) => {
                setDisplayOrder(prev => ({
                  ...prev,
                  [currentTableId]: newOrder
                }));
                markSettingsDirty(currentTableId, 'displayOrder');
              }}
              showColumnTotals={showColumnTotals}
              onToggleColumnTotals={(tableId) => {
                setShowColumnTotals(prev => ({
                  ...prev,
                  [tableId]: !(prev[tableId] ?? true)
                }));
                markSettingsDirty(tableId, 'showColumnTotals');
              }}
              propagateMostRecent={propagateMostRecent}
              onTogglePropagateMostRecent={(tableId) => {
                setPropagateMostRecent(prev => ({
                  ...prev,
                  [tableId]: !(prev[tableId] ?? false)
                }));
                markSettingsDirty(tableId, 'propagateMostRecent');
              }}
              isHidden={hiddenTables[currentTableId] ?? false}
              onToggleHidden={(tableId) => {
                setHiddenTables(prev => ({
                  ...prev,
                  [tableId]: !(prev[tableId] ?? false)
                }));
                markSettingsDirty(tableId, 'hidden');
              }}
              columnPolicies={columnPolicies[currentTableId] ?? currentTable?.column_policies ?? null}
              onColumnPoliciesChange={(newPolicies) => {
                setColumnPolicies(prev => ({
                  ...prev,
                  [currentTableId]: newPolicies
                }));
                markSettingsDirty(currentTableId, 'columnPolicies');
              }}
              reportingDateMode={reportingDateMode[currentTableId] ?? currentTable?.reporting_date_mode ?? null}
              onReportingDateModeChange={(newMode) => {
                setReportingDateMode(prev => ({
                  ...prev,
                  [currentTableId]: newMode
                }));
                markSettingsDirty(currentTableId, 'reportingDateMode');
              }}
              classification={classification[currentTableId] ?? currentTable?.classification ?? null}
              onClassificationChange={(newClassification) => {
                setClassification(prev => ({
                  ...prev,
                  [currentTableId]: newClassification
                }));
                markSettingsDirty(currentTableId, 'classification');
              }}
              entityResolution={entityResolution[currentTableId] ?? currentTable?.entityResolution ?? null}
              onEntityResolutionChange={(newConfig) => {
                setEntityResolution(prev => ({
                  ...prev,
                  [currentTableId]: newConfig
                }));
                markSettingsDirty(currentTableId, 'entityResolution');
              }}
              extractionPrompt={extractionPrompts[currentTableId] ?? currentTable?.extraction_prompt ?? ''}
              onExtractionPromptChange={(newPrompt) => {
                setExtractionPrompts(prev => ({
                  ...prev,
                  [currentTableId]: newPrompt
                }));
                markSettingsDirty(currentTableId, 'extractionPrompt');
              }}
              autoAllocateProfile={autoAllocateProfile[currentTableId] ?? !!currentTable?.auto_allocate_profile}
              onToggleAutoAllocateProfile={(tableId) => {
                setAutoAllocateProfile(prev => ({
                  ...prev,
                  [tableId]: !(prev[tableId] ?? !!currentTable?.auto_allocate_profile)
                }));
                markSettingsDirty(tableId, 'autoAllocateProfile');
              }}
              onExternalTablePatched={(patch) => {
                setTables(prev => prev.map(t => (
                  t.id === currentTableId ? { ...t, ...patch } : t
                )));
                if (patch.display_columns) {
                  setDisplaySettings(prev => ({
                    ...prev,
                    [currentTableId]: patch.display_columns
                  }));
                }
                if (patch.display_order) {
                  setDisplayOrder(prev => ({
                    ...prev,
                    [currentTableId]: patch.display_order
                  }));
                }
              }}
              exportDraftRef={exportDraftRef}
              onExportDraftDirtyChange={handleExportDraftDirtyChange}
            />
          ) : null}
        </Box>

        {/* Right Filter Panel - Positioned against right edge with small gap */}
        {effectiveViewMode === 'data' && !showingUnusableTable && (
          <Box sx={{
            height: '98%',
            display: { xs: 'none', md: 'block' },
            position: 'relative',
            borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
            width: filtersWidth,
            maxWidth: filtersWidth,
            flexShrink: 0,
            ml: 'auto',
          }}>
            {!showFilters && (
              <Box sx={{ pt: 1.5, display: 'flex', justifyContent: 'center' }}>
                <Tooltip title="Expand filters" placement="left">
                  <IconButton size="small" onClick={() => setShowFilters(true)}>
                    <KeyboardDoubleArrowLeftIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            <Box sx={{
              p: 2,
              pr: 2,
              height: '100%',
              display: showFilters ? 'block' : 'none',
            }}>
              <Paper sx={{
                p: 2,
                height: '100%',
                borderRadius: 1.5,
                border: '1px solid rgba(255, 255, 255, 0.03)',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: 1,
                transition: 'all 0.2s ease',
                mr: 1,
                overflow: 'hidden',
              }}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2
                }}>
                  <Typography variant="subtitle1" fontWeight="600" sx={{ color: 'text.primary' }}>
                    Filters
                  </Typography>
                  <Tooltip title="Collapse filters">
                    <IconButton
                      size="small"
                      onClick={() => setShowFilters(false)}
                      sx={{
                        p: 0.5,
                        color: 'text.secondary',
                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
                      }}
                    >
                      <KeyboardDoubleArrowRightIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                
                {currentTable ? (
                  <Box sx={{ 
                    height: 'calc(100% - 48px)',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <DynamicFilters
                      tableSchema={currentTable}
                      onSubmit={handleSubmit}
                      loading={loading}
                      error={error}
                    />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Select a table to use filters
                  </Typography>
                )}
              </Paper>
            </Box>
          </Box>
        )}
      </Box>

      {/* Dialogs */}
      <Dialog 
        open={dialog.open} 
        onClose={handleDismissDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: 'background.paper',
            backgroundImage: 'none'
          }
        }}
      >
        <DialogTitle sx={{ 
          m: 0, 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {dialog.type === 'attribute' && (
            <>
              <Typography variant="h6" component="div" fontWeight={600}>
                Manage Attribute Categories
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    // Trigger the newCategoryDialog in ManageAttributes
                    const event = new CustomEvent('open-add-category-dialog');
                    document.dispatchEvent(event);
                  }}
                  sx={{ 
                    mr: 1,
                    borderRadius: '6px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                    textTransform: 'none',
                    px: 2.5,
                    py: 1,
                    fontWeight: 500
                  }}
                >
                  Add Category
                </Button>
                <HelpButton 
                  docPath="category/2-understanding--managing-metadata" 
                  tooltip="Attribute Documentation"
                />
                <IconButton
                  aria-label="close"
                  onClick={handleCloseDialog}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </>
          )}
          {dialog.type === 'data_profiles' && (
            <>
              <Typography variant="h6" component="div">
                {currentTable?.name} Data Profiles
              </Typography>
              <Box>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const event = new CustomEvent('create-profile');
                    document.dispatchEvent(event);
                  }}
                  sx={{ mr: 1 }}
                >
                  Create New Profile
                </Button>
                <IconButton
                  aria-label="close"
                  onClick={handleCloseDialog}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </>
          )}
          {dialog.type === 'upload' && 'Upload Files'}
          {dialog.type === 'files' && 'Uploaded Files'}
          {dialog.type === 'createTable' && (
            <>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>Create New Table</Typography>
              <HelpButton 
                docPath="data-page/working-with-tables/creating-a-table" 
                tooltip="Table Creation Documentation"
              />
              <IconButton
                aria-label="close"
                onClick={handleDismissDialog}
                disabled={dialogBusy}
              >
                <CloseIcon />
              </IconButton>
            </>
          )}
          
          {dialog.type !== 'attribute' && dialog.type !== 'data_profiles' && dialog.type !== 'createTable' && (
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {dialog.type === 'attribute' && (
            <ManageAttributes 
              companyName={companyName} 
            />
          )}
                    
          {dialog.type === 'createTable' && (
            <CreateTableForm
              onCancel={handleDismissDialog}
              onSubmittingChange={setDialogBusy}
              initialDraft={savedDraftRef.current}
              onDraftChange={handleCreateTableDraftChange}
              onManageConnections={handleManageConnectionsFromWizard}
              onTableCreated={(newTable) => {
                // Add the new table to the tables list — unless the live listener has
                // already delivered its document, which can land before this callback.
                // Recorded at once, not when the listener next delivers it: a query started
                // before then would otherwise look like a query for a table that has since
                // been replaced, and its answer would be dropped.
                tableIncarnationsRef.current[newTable.id] = incarnationOf(newTable);
                setTables(prevTables => {
                  const listed = prevTables.find(t => t.id === newTable.id);
                  if (!listed) return [...prevTables, newTable];
                  if (isTableUsable(listed)) return prevTables;
                  // So far the listener has delivered only the creation's name reservation.
                  return prevTables.map(t => (t.id === newTable.id ? newTable : t));
                });
                // Close the dialog before the guard, so the two dialogs never stack
                handleCloseDialog();
                // Select the new table
                requestSettingsNavigation(() => {
                  setCurrentTableId(newTable.id);
                  setViewMode('data');
                });
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Table: {tableToDelete?.name}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteConfirmationText(tableToDelete)}
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <UnsavedSettingsDialog
        open={unsavedSettingsDialogOpen}
        saving={saving}
        onSaveAndContinue={handleSaveSettingsAndContinue}
        onDiscard={handleDiscardSettings}
        onStay={handleStayOnSettings}
      />

      {/* The Create Table wizard's draft lives only in memory: a dismissal
          with anything entered asks before it is lost (M211). */}
      <Dialog
        open={discardWizardOpen}
        onClose={() => setDiscardWizardOpen(false)}
        aria-labelledby="discard-table-dialog-title"
      >
        <DialogTitle id="discard-table-dialog-title">Discard this table?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The details you have entered for this table will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardWizardOpen(false)}>Keep editing</Button>
          <Button onClick={handleCloseDialog} color="error">Discard</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Data;
